import { FetchError } from "@selector/shared";
import { SyntenyTypeEnum, TrackEnum, TrackTextEnum } from "@selector/shared/openapi";
import pluralize from "pluralize";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useErrorBoundary } from "react-error-boundary";
import {
  ActionButton,
  Card,
  CardRowSimple,
  CardSection,
  DialogComponent,
  HStack,
  notify,
  Tag,
  TriggerButton,
  TriggerButtonGroup,
  VStack,
} from "widgets";
import { useLongLoading } from "widgets/hooks";
import { colors } from "widgets/utils";
import { useShallow } from "zustand/shallow";

import { fetchFeatureInfo } from "../../../api/fetch.ts";
import { syntenyViewerDefaultTracks } from "../../../settings.ts";
import type { MemberItem } from "../../../store/slices/main-slice.ts";
import { useGenomeViewerStore } from "../../../store/store.ts";
import { type FeatureInfo, type MainThreadHoverMessage } from "../../../types.ts";
import type { TrackConfig } from "../../../utils/tracks/create-tracks.ts";
import { getGenomicFeatureTypeFromTrackType } from "../../../utils/type-conversion.ts";
import { getSortedParalogsData } from "../utils/get-sorted-paralogs-data.ts";
import {
  type GenomeMember,
  getAlignmentMembersByGenomeOrThrow,
  getFeaturesFamilyMembersByGenomeOrThrow,
  getSequenceFamilyMembersByGenomeOrThrow,
} from "../utils/process-genome-data.ts";
import { useClose } from "./hooks/use-close.ts";
import { useGroup } from "./hooks/use-group.ts";
import { ItemButton } from "./ItemButton.tsx";
import { OpenGenomeViewerButton } from "./OpenGenomeViewerButton.tsx";
import { OpenSummaryPageButton } from "./OpenSummaryPageButton.tsx";
import { SelectRangeButton } from "./SelectRangeButton.tsx";
import { Skeleton } from "./Skeleton.tsx";
import { TrackFeatureTooltip } from "./TrackFeatureTooltip.tsx";
import { TrackNuFeatureTooltip } from "./TrackNuFeatureTooltip.tsx";
import { TrackRegionTooltip } from "./TrackRegionTooltip.tsx";

// eslint-disable-next-line complexity
export function InfoPopover({ selectedTrackType }: { selectedTrackType: TrackEnum | undefined }) {
  const { showBoundary } = useErrorBoundary();
  const createTracks = useGenomeViewerStore((state) => state.createTracks);
  const refreshTracks = useGenomeViewerStore((state) => state.refreshTracks);
  const handleFeaturePopover = useGenomeViewerStore((state) => state.handleFeaturePopover);
  const refreshMain = useGenomeViewerStore((state) => state.refreshMain);
  const updateTrackContigId = useGenomeViewerStore((state) => state.updateTrackContigId);
  const align = useGenomeViewerStore((state) => state.align);
  const sendSyntenyRenderMessage = useGenomeViewerStore((state) => state.sendSyntenyRenderMessage);

  const alignedFamily = useGenomeViewerStore((state) => state.alignedFamily);
  const syntenyId = useGenomeViewerStore((state) => state.syntenyOrganism.id);
  const syntenyWorker = useGenomeViewerStore((state) => state.syntenyWorker);
  const activeSyntenyType = useGenomeViewerStore((state) => state.activeSyntenyType);
  const show = useGenomeViewerStore((state) => state.showFeaturePopover);
  const memberItem: (MemberItem & Partial<MainThreadHoverMessage>) | undefined = useGenomeViewerStore(
    useShallow((state) => {
      const menuData = state.contextualMenuData;
      if (menuData && menuData.id && menuData.trackId && menuData.start && menuData.stop) {
        const track = state.tracks.get(menuData.trackId);
        if (track) {
          const group = state.trackGroups.get(track?.groupId);
          if (group) {
            return {
              ...menuData,
              featureId: menuData.id, // TypeScript is dumb, we need to reset it so that he's happy.
              trackId: menuData.trackId, // TypeScript is dumb, we need to reset it so that he's happy.
              familyId: menuData.orthologId,
              contigId: group.contigId,
              trackGroupId: group.groupId,
              start: menuData.start,
              stop: menuData.stop,
            };
          }
        }
      }
      return undefined;
    }),
  );

  const [paralogs, setParalogs] = useState<GenomeMember[]>([]);
  const [sameQty, setSameQty] = useState<number>();
  const [isLoading, setIsLoading] = useState(true); // We'll load
  const [info, setInfo] = useState<FeatureInfo | undefined>(undefined);

  const close = useClose();
  const isLongLoading = useLongLoading(isLoading, 300);
  const { group, track } = useGroup(memberItem);

  const { organismId, contigName, graphics, groupId } = group || {};

  useEffect(() => {
    // reset
    setParalogs([]);
    setSameQty(undefined);
    setInfo(undefined);
    setIsLoading(true);

    if (show) {
      async function getGenomeCount() {
        if (selectedTrackType && memberItem?.featureId && syntenyId && organismId) {
          const featureId = memberItem.featureId;
          if (selectedTrackType !== TrackEnum.SEQUENCE) {
            setInfo(await fetchFeatureInfo(getGenomicFeatureTypeFromTrackType(selectedTrackType), featureId));
          }
          let homologs: Map<number, GenomeMember[]>;
          if (selectedTrackType === TrackEnum.SEQUENCE && memberItem?.familyId) {
            // Only we the memberItem is a region, in a Pangenome dataset
            homologs = await getSequenceFamilyMembersByGenomeOrThrow(syntenyId, memberItem.familyId);
          } else {
            homologs =
              memberItem.familyId && activeSyntenyType
                ? await getFeaturesFamilyMembersByGenomeOrThrow(
                    memberItem.familyId,
                    syntenyId,
                    activeSyntenyType,
                    featureId,
                  )
                : await getAlignmentMembersByGenomeOrThrow(
                    syntenyId,
                    getGenomicFeatureTypeFromTrackType(selectedTrackType),
                    featureId,
                  );
          }

          setParalogs(getSortedParalogsData(homologs, organismId, memberItem.featureId));
          // Count distinct genomes from the already grouped result and include the current genome.
          const genomeCount = homologs.size + (homologs.has(organismId) ? 0 : 1);
          return genomeCount;
        }
        return 0;
      }

      getGenomeCount()
        .then((genomeCount) => {
          setIsLoading(false);
          setSameQty(genomeCount);
        })
        .catch((error: Error | FetchError) => {
          if (error instanceof FetchError && error.statusCode === 401) {
            showBoundary(error);
          }

          notify.error({ title: "Could not load feature info", children: error.message });
        });
    }
  }, [
    alignedFamily?.familyId,
    memberItem,
    activeSyntenyType,
    selectedTrackType,
    show,
    syntenyId,
    showBoundary,
    organismId,
  ]);

  useEffect(() => {
    document.body.addEventListener("pointerdown", handleFeaturePopover, true);
    return () => {
      document.body.removeEventListener("pointerdown", handleFeaturePopover, true);
    };
  }, [handleFeaturePopover]);

  const handleAlign = useCallback(() => {
    if (memberItem) {
      align(memberItem)
        .then(close)
        .catch((error: Error) => {
          notify.error({ title: "Alignment failed", children: error.message });
        });
    }
  }, [align, close, memberItem]);

  const handleAlignOther = useCallback(
    (id: number, contigId: number, start: number, stop: number, familyId?: number) => {
      if (graphics && track && memberItem) {
        updateTrackContigId(track.groupId, contigId, (memberItem.start ?? 0) + graphics.baseShift - start);
        refreshTracks();
        refreshMain();

        // We're aligned with a member of the same family of the currently highlighted family
        if (alignedFamily?.familyId === familyId) {
          align({
            featureId: id,
            trackId: memberItem.trackId,
            familyId,
            trackGroupId: track.groupId,
            contigId,
            start,
            stop,
          }).catch((error: Error) => {
            notify.error({ title: "Alignment failed", children: error.message });
          });
        } else {
          // Delete the alignedFamily Member because we moved the track somewhere else, unrelated to the current alignment.
          // If we keep the members in the family, we will be desynchronized from what on screen, and if you align from a
          // member of the still aligned family on screen, it won't be able to recall this track to the correct members
          // because for the app, the members is still in the aligned family, which is visually not true anymore.
          if (alignedFamily) {
            // we should not mutate a returned value according to react compiler, but we will.
            // eslint-disable-next-line react-compiler/react-compiler, react-hooks/immutability
            alignedFamily.members = alignedFamily?.members.filter((m) => m.trackId !== memberItem.trackId);
            useGenomeViewerStore.setState({ alignedFamily });
            sendSyntenyRenderMessage(syntenyWorker);
          }
        }
      }
      close();
    },
    [
      graphics,
      track,
      memberItem,
      close,
      updateTrackContigId,
      refreshTracks,
      refreshMain,
      alignedFamily,
      align,
      sendSyntenyRenderMessage,
      syntenyWorker,
    ],
  );

  const handleNewTrack = useCallback(
    (id: number, contigId: number, start: number, stop: number, familyId?: number) => {
      if (track && memberItem && organismId) {
        const newGroup = {
          contigId,
          genomeId: organismId,
          // This is where we pre-shift the track
          toBase: (memberItem.start ?? 0) + (graphics?.baseShift ?? 0) - start,
        };

        const newTrack: TrackConfig =
          track.trackType === TrackEnum.NUCLEOTIDE_FEATURE
            ? {
                trackType: track.trackType,
                group: newGroup,
                options: {
                  subType: track.subType!,
                  color: track.color,
                  text: TrackTextEnum.INNER,
                  isVisible: true,
                },
              }
            : {
                trackType: track.trackType,
                group: newGroup,
              };

        const newTrackIdSet = createTracks([newTrack], showBoundary, groupId);

        // Manually updating the aligned family
        const to = newTrackIdSet.values().next().value;

        if (to && alignedFamily && alignedFamily?.familyId === familyId) {
          alignedFamily.members.push({ featureId: id, trackId: to.trackId, start, stop });
          useGenomeViewerStore.setState({ alignedFamily });
        }

        //! As we are pre-shifting tracks and manually updating the aligned family, there is no need to align here.
        refreshTracks();
      }
    },
    [
      alignedFamily,
      createTracks,
      graphics?.baseShift,
      groupId,
      memberItem,
      organismId,
      refreshTracks,
      showBoundary,
      track,
    ],
  );

  const showSkeleton =
    isLoading || isLongLoading || !memberItem || (selectedTrackType !== TrackEnum.SEQUENCE && info == undefined);
  const canAlign = (sameQty ?? 0) > 1;

  return createPortal(
    <div
      id="genome-viewer-contextual-menu"
      data-testid="genome-viewer-contextual-menu"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        display: "none",
        zIndex: 10,
      }}
    >
      <DialogComponent
        key={`feature-${memberItem?.featureId}`}
        title={memberItem?.name}
        subtitle={
          selectedTrackType === TrackEnum.NUCLEOTIDE_FEATURE
            ? "DNA Feature"
            : syntenyViewerDefaultTracks.find((trackType) => trackType.type === selectedTrackType)?.name
        }
        titleTruncateMiddle
        asPanelHeader
        contentStyle={{ height: "fit-content", minWidth: 350, maxWidth: 700 }}
        style={{ maxWidth: 700 }}
        actions={
          showSkeleton
            ? undefined
            : [
                paralogs.length > 0 && selectedTrackType !== TrackEnum.SEQUENCE ? (
                  <Tag
                    key="paralog-count"
                    intentColor={colors.secondaryMain}
                    tooltip={{ title: "Syntelogs in This Genome" }}
                  >
                    {/* + itself */}
                    {paralogs.length + 1}
                  </Tag>
                ) : null,
                memberItem && info != undefined && selectedTrackType !== TrackEnum.SEQUENCE ? (
                  <TriggerButtonGroup key={"open-group"}>
                    <OpenSummaryPageButton
                      key={"open-summary-page"}
                      id={info.protein?.id ?? info.transcript?.id ?? memberItem.featureId}
                      trackId={memberItem.trackId}
                    />
                    <OpenGenomeViewerButton
                      key={"open-in-genome-viewer"}
                      id={info.protein?.id ?? info.transcript?.id ?? memberItem.featureId}
                      trackId={memberItem.trackId}
                    />
                  </TriggerButtonGroup>
                ) : null,
                memberItem && memberItem.start && memberItem.stop ? (
                  <TriggerButtonGroup key={"select-range-group"}>
                    <SelectRangeButton
                      key={"select-range"}
                      range={{ start: memberItem.start, stop: memberItem.stop }}
                      trackGroupId={memberItem.trackGroupId}
                      trackId={memberItem.trackId}
                      onClose={close}
                    />
                  </TriggerButtonGroup>
                ) : null,
              ]
        }
      >
        <Card noBorder noPadding>
          {showSkeleton ? (
            <CardSection>
              <Skeleton />
            </CardSection>
          ) : (
            <>
              <CardSection>
                {!!memberItem && selectedTrackType === TrackEnum.SEQUENCE ? (
                  <TrackRegionTooltip data={memberItem} contigName={contigName} />
                ) : info != undefined ? (
                  selectedTrackType === TrackEnum.NUCLEOTIDE_FEATURE ? (
                    <TrackNuFeatureTooltip info={info} currentTrackType={selectedTrackType} contigName={contigName} />
                  ) : (
                    <TrackFeatureTooltip info={info} currentTrackType={selectedTrackType} contigName={contigName} />
                  )
                ) : null}
              </CardSection>
              <>
                {(selectedTrackType === TrackEnum.PROTEIN_LOCATION ||
                  activeSyntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT) && (
                  <CardRowSimple>
                    <ActionButton
                      isDisabled={!canAlign}
                      onPress={handleAlign}
                      isLoading={isLoading}
                      label={
                        canAlign || isLongLoading
                          ? `Align ${sameQty ?? 0} ${pluralize("Genome", sameQty)}`
                          : selectedTrackType === TrackEnum.SEQUENCE
                            ? "No Syntenic Regions in Other Genomes"
                            : "No Syntelogs in Other Genomes"
                      }
                    />
                  </CardRowSimple>
                )}
                {paralogs.length > 0 && (
                  <CardRowSimple
                    label={
                      activeSyntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT
                        ? "Other Syntenic Regions"
                        : "Other Syntelogs in This Genome"
                    }
                  >
                    <VStack>
                      {paralogs.map((member) => (
                        <ItemButton
                          key={String(member.locationId)}
                          member={member}
                          onPress={() =>
                            handleAlignOther(
                              member.locationId,
                              member.contigId,
                              member.start,
                              member.stop,
                              member.familyId,
                            )
                          }
                          hoverElement={
                            <HStack spacing={1}>
                              <TriggerButton
                                icon="start"
                                label="Go"
                                onPress={() =>
                                  handleAlignOther(
                                    member.locationId,
                                    member.contigId,
                                    member.start,
                                    member.stop,
                                    member.familyId,
                                  )
                                }
                              />
                              <TriggerButton
                                icon="variable_add"
                                label="Add Track"
                                onPress={() =>
                                  handleNewTrack(
                                    member.locationId,
                                    member.contigId,
                                    member.start,
                                    member.stop,
                                    member.familyId,
                                  )
                                }
                              />
                            </HStack>
                          }
                        />
                      ))}
                    </VStack>
                  </CardRowSimple>
                )}
              </>
            </>
          )}
        </Card>
      </DialogComponent>
    </div>,
    document.body,
  );
}
